import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import OperationModesScheduleCard from './OperationModesScheduleCard';
import OperationModesSchedulesEditor, { OperationModesSchedulesEditorRef } from './OperationModesSchedulesEditor';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DeleteConfirmationDialog } from '@/components/dialog/DeleteConfirmationDialog';
import {
    getActiveOnTranslation,
    getSortedOperationModeSchedules,
    getSortedOperationModesSchedules,
    removeOperationModesSchedule,
    upsertOperationModesSchedule
} from '@features/shopautomation/operationModesSchedulesUtils.ts';
import { OperationModesScheduleGroup, OperationModesSchedulesProps } from '@features/shopautomation/types.ts';
import { v4 as uuidv4 } from 'uuid';
import { useUnsavedChangesGuard } from '@core/hooks/useUnsavedChangesGuard.ts';
import { useSettingsStore } from '@core/store';

interface Props extends OperationModesSchedulesProps {
    schedulesEnabled: boolean;
    openEditorRef?: React.RefObject<(() => void) | null>;
}

const OperationModesSchedules: React.FC<Props> = ({
    value,
    operationModes,
    activeOperationModes,
    onChange,
    schedulesEnabled,
    openEditorRef
}) => {
    const { t } = useTranslation();
    const { isMobile } = useSettingsStore();
    const [schedules, setSchedules] = useState<OperationModesScheduleGroup[]>(value);
    const [selected, setSelected] = useState<OperationModesScheduleGroup | null>(null);
    const [selectedIsExpired, setSelectedIsExpired] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const editorScrollRef = useRef<HTMLDivElement>(null);
    const editorComponentRef = useRef<OperationModesSchedulesEditorRef>(null);
    const lastSentValueRef = useRef<OperationModesScheduleGroup[]>(value);

    const [expiredExpanded, setExpiredExpanded] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<OperationModesScheduleGroup | null>(null);

    const defaultOperationModesSchedules = {
        placeholderId: uuidv4(),
        activeOn: 'standard',
        operationModeSchedules: [
            {
                from: '00:00',
                operationMode: 'Unmanned'
            }
        ]
    };

    useEffect(() => {
        if (lastSentValueRef.current === value) return;
        setSchedules(value);
        lastSentValueRef.current = value;
    }, [value]);

    useEffect(() => {
        if (selected === null) return;
        setTimeout(() => {
            if (!editorScrollRef.current) return;
            const scrollContainer = document.querySelector('main') as HTMLElement;
            if (!scrollContainer) return;
            const top =
                editorScrollRef.current.getBoundingClientRect().top -
                scrollContainer.getBoundingClientRect().top +
                scrollContainer.scrollTop -
                24;
            scrollContainer.scrollTo({ top, behavior: 'smooth' });
        }, 50);
    }, [selected]);

    const select = useCallback((item?: OperationModesScheduleGroup, isExpired = false) => {
        setSelected(
            item
                ? structuredClone(item)
                : schedules.length > 0
                  ? ({} as OperationModesScheduleGroup)
                  : defaultOperationModesSchedules
        );
        setIsUpdate(!!item);
        setSelectedIsExpired(isExpired);
    }, []);

    const deselect = useCallback(() => {
        setSelected(null);
        setSelectedIsExpired(false);
    }, []);

    const isDirty = useCallback(() => !!editorComponentRef.current?.isDirty, []);

    const saveCurrentEditor = useCallback(async () => {
        const saved = await editorComponentRef.current?.save();
        return !!saved;
    }, []);

    const { guard } = useUnsavedChangesGuard({ isDirty, save: saveCurrentEditor });

    const trySelect = useCallback(
        (item?: OperationModesScheduleGroup, isExpired = false) => {
            guard(() => select(item, isExpired));
        },
        [guard, select]
    );

    const handleCancel = useCallback(() => {
        guard(() => deselect());
    }, [guard, deselect]);

    useEffect(() => {
        if (openEditorRef) openEditorRef.current = () => trySelect();
    }, [openEditorRef, trySelect]);

    const save = useCallback(
        (item: OperationModesScheduleGroup) => {
            setSchedules((prev) => {
                const { schedules: newSchedules, isNew } = upsertOperationModesSchedule(prev, item);
                lastSentValueRef.current = newSchedules;
                onChange(newSchedules, item, isNew ? 'add' : 'update');
                return newSchedules;
            });
        },
        [onChange]
    );

    const handleDeleteClick = useCallback((schedule: OperationModesScheduleGroup) => {
        setDeleteTarget(schedule);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (!deleteTarget) return;
        setSchedules((prev) => {
            const newSchedules = removeOperationModesSchedule(prev, deleteTarget);
            if (newSchedules === prev) return prev;

            lastSentValueRef.current = newSchedules;
            onChange(newSchedules, deleteTarget, 'delete');
            return newSchedules;
        });
        setDeleteTarget(null);
    }, [deleteTarget, onChange]);

    const translateActiveOn = useCallback(
        (schedule: OperationModesScheduleGroup) => getActiveOnTranslation(schedule, t),
        [t]
    );

    const [activeSchedules, expiredSchedules] = useMemo(() => getSortedOperationModesSchedules(schedules), [schedules]);

    const unavailableDaysDates = useMemo(
        () => schedules.flatMap((x) => (x.activeOn ? x.activeOn.split(',') : [])),
        [schedules]
    );

    const scheduleListSx = isMobile
        ? {}
        : {
            border: '1px solid var(--sidebar-divider)',
            borderRadius: '4px',
            backgroundColor: 'background.paper',
            px: 2,
            py: 0.5
        };

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Box sx={{ pb: selected !== null ? 2 : 0 }}>
                {activeSchedules.length > 0 && (
                    <Box className={isMobile ? undefined : 'operation-modes-schedules-card'} sx={scheduleListSx}>
                        {activeSchedules.map((group, index) => (
                            <OperationModesScheduleCard
                                key={group.placeholderId || group.id || index}
                                operationModesSchedule={group}
                                getActiveOnTranslation={translateActiveOn}
                                getSortedOperationModeSchedules={getSortedOperationModeSchedules}
                                onEdit={() => trySelect(group, false)}
                                onDelete={() => handleDeleteClick(group)}
                                schedulesEnabled={schedulesEnabled}
                                scheduleLength={activeSchedules.length}
                            />
                        ))}
                    </Box>
                )}

                {expiredSchedules.length > 0 && (
                    <Box sx={{ mt: 5 }}>
                        <Box
                            className="expired-schedules-heading"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 2
                            }}
                        >
                            <img
                                src="/images/icons/slash-circle.svg"
                                alt="slash circle"
                                className="expired-icon"
                                style={{ width: '15px', height: '15px', marginTop: '-1px' }}
                            />
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    fontFamily: 'Wanzl_Etelka Medium',
                                    letterSpacing: '1.2px',
                                    textTransform: 'uppercase',
                                    color: 'text.primary'
                                }}
                            >
                                {t('expiredSchedules')}
                            </Typography>
                        </Box>

                        <Box
                            className={isMobile ? undefined : 'operation-modes-schedules-card'}
                            sx={
                                isMobile
                                    ? {}
                                    : {
                                        border: '1px solid var(--sidebar-divider)',
                                        borderRadius: '4px',
                                        backgroundColor: 'background.paper'
                                    }
                            }
                        >
                            {expiredExpanded && (
                                <Box sx={isMobile ? { mb: 1 } : { px: 2, py: 0.5 }}>
                                    {expiredSchedules.map((group, index) => (
                                        <OperationModesScheduleCard
                                            key={group.placeholderId || group.id || index}
                                            operationModesSchedule={group}
                                            getActiveOnTranslation={translateActiveOn}
                                            getSortedOperationModeSchedules={getSortedOperationModeSchedules}
                                            onEdit={() => trySelect(group, true)}
                                            onDelete={() => handleDeleteClick(group)}
                                            schedulesEnabled={schedulesEnabled}
                                            isExpired
                                        />
                                    ))}
                                </Box>
                            )}

                            <Box
                                onClick={() => setExpiredExpanded((prev) => !prev)}
                                sx={{
                                    py: 1.25,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'action.hover' }
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        letterSpacing: '1px',
                                        textTransform: 'uppercase',
                                        color: 'text.disabled',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    {expiredExpanded ? t('showLess') : t('showMore')}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {selected !== null && (
                    <Box ref={editorScrollRef}>
                        <OperationModesSchedulesEditor
                            ref={editorComponentRef}
                            value={selected}
                            unavailableDaysDates={unavailableDaysDates}
                            operationModes={operationModes}
                            activeOperationModes={activeOperationModes}
                            onSave={save}
                            onClose={handleCancel}
                            isExpired={selectedIsExpired}
                            isUpdate={isUpdate}
                        />
                    </Box>
                )}
            </Box>

            <DeleteConfirmationDialog
                open={deleteTarget !== null}
                heading={t('deleteHeading')}
                message={t('deleteItemMessage')}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteTarget(null)}
            />
        </LocalizationProvider>
    );
};

export default OperationModesSchedules;
